import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, useTheme, TextField, InputAdornment, FormControlLabel, Checkbox, Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon, Inventory2 as InventoryIcon, Search as SearchIcon,
  FileDownload as ExcelIcon, PictureAsPdf as PdfIcon, Print as PrintIcon,
  WarningAmberRounded as WarningIcon, ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmtUnitPrice } from '../../utils/formatNumber';

interface ChainEntry {
  unitId: number;
  unitCode: string;
  unitName: string;
  unitNameAr: string;
  conversionFactor: number;
  stepFactor: number;
  nextUnitName: string | null;
  nextUnitNameAr: string | null;
  isBase: boolean;
  salePrice: number;
  purchasePrice: number;
}

interface ReportRow {
  itemId: number; itemCode: string; itemName: string; itemNameAr: string;
  categoryName: string; categoryNameAr: string; itemType: string;
  baseUnitId: number; baseUnitCode: string; baseUnitName: string; baseUnitNameAr: string;
  units: ChainEntry[];
  barcode: string | null; isActive: boolean;
  warningCode: string | null; warningMessage: string | null; warningMessageAr: string | null;
}

interface ReportResult {
  generatedAt: string;
  rows: ReportRow[];
  totalItems: number;
  totalSuspicious: number;
  maxUnitsPerItem: number;
  baseUnitDistribution: Record<string, number>;
}

const ItemsUnitsReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [onlySuspicious, setOnlySuspicious] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('includeInactive', includeInactive.toString());
      if (onlySuspicious) params.append('onlySuspicious', 'true');
      const { data } = await axiosInstance.get(`/ItemsUnitsReport?${params.toString()}`);
      setResult(data);
      toast.success(isRTL ? 'تم التحميل' : 'Loaded');
    } catch {
      toast.error(isRTL ? 'فشل التحميل' : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const fmtNum = (n: number | null | undefined) => {
    if (n == null) return '-';
    const num = Number(n);
    const s = num.toFixed(7).replace(/\.?0+$/, '');
    return s === '' || s === '-' ? '0' : s;
  };
  const reportTitle = isRTL ? 'تقرير الأصناف ووحدات القياس' : 'Items & Units of Measure Report';
  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;

  const filteredRows = useMemo(() => {
    if (!result) return [];
    if (!searchTerm) return result.rows;
    const term = searchTerm.toLowerCase();
    return result.rows.filter(r =>
      r.itemCode.toLowerCase().includes(term) ||
      r.itemName.toLowerCase().includes(term) ||
      r.itemNameAr.includes(searchTerm) ||
      (r.categoryNameAr || '').includes(searchTerm) ||
      (r.categoryName || '').toLowerCase().includes(term) ||
      (r.barcode || '').includes(searchTerm)
    );
  }, [result, searchTerm]);

  const maxUnits = result?.maxUnitsPerItem || 1;

  const renderChainText = (r: ReportRow): string => {
    return r.units.map((u, i) => {
      const name = isRTL ? (u.unitNameAr || u.unitName) : u.unitName;
      const next = isRTL ? (u.nextUnitNameAr || u.nextUnitName) : u.nextUnitName;
      if (u.isBase) {
        return `${name} (${isRTL ? 'الأساسية' : 'Base'} = 1)`;
      }
      if (next) {
        return `${name} = ${fmtNum(u.stepFactor)} ${next}`;
      }
      return `${name} = ${fmtNum(u.conversionFactor)} ${isRTL ? r.baseUnitNameAr || r.baseUnitName : r.baseUnitName}`;
    }).join(' → ');
  };

  const exportToExcel = async () => {
    if (!result) return;
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(reportTitle);
      if (companyInfo?.logoBase64) {
        try {
          const img = wb.addImage({
            base64: companyInfo.logoBase64.split(',').pop() || companyInfo.logoBase64,
            extension: 'png'
          });
          ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } });
        } catch {}
      }

      // Build dynamic headers: fixed + repeated unit blocks (Unit N, Step Factor N, Sale N, Purchase N)
      const fixedHeaders = [
        isRTL ? 'كود الصنف' : 'Item Code',
        isRTL ? 'الاسم' : 'Name',
        isRTL ? 'الاسم بالعربي' : 'Name (Ar)',
        isRTL ? 'المجموعة' : 'Category',
        isRTL ? 'الوحدة الأساسية' : 'Base Unit',
      ];
      const unitHeaders: string[] = [];
      for (let i = 1; i <= maxUnits; i++) {
        unitHeaders.push(
          isRTL ? `وحدة ${i}` : `Unit ${i}`,
          isRTL ? `معامل ${i}→` : `Factor ${i}→`,
          isRTL ? `يساوي ${i}` : `Equals ${i}`,
          isRTL ? `سعر بيع ${i}` : `Sale ${i}`,
          isRTL ? `سعر شراء ${i}` : `Purchase ${i}`,
        );
      }
      const tailHeaders = [
        isRTL ? 'CF تراكمي للأكبر' : 'Top CF (cum.)',
        isRTL ? 'تحذير' : 'Warning',
        isRTL ? 'ملاحظة (تعديل يدوي)' : 'Notes (manual)',
      ];
      const headers = [...fixedHeaders, ...unitHeaders, ...tailHeaders];
      const totalCols = headers.length;

      const lastColLetter = (n: number) => {
        let s = '';
        while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
        return s;
      };
      const lastL = lastColLetter(totalCols);

      ws.mergeCells(`C1:${lastL}1`);
      ws.getCell('C1').value = compName || '';
      ws.getCell('C1').font = { size: 18, bold: true, color: { argb: 'FF1565C0' } };
      ws.getCell('C1').alignment = { horizontal: 'center' };

      ws.mergeCells(`A3:${lastL}3`);
      const tc = ws.getCell('A3');
      tc.value = reportTitle;
      tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      tc.alignment = { horizontal: 'center' };
      tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };

      ws.mergeCells(`A4:${lastL}4`);
      ws.getCell('A4').value = `${isRTL ? 'تاريخ التقرير' : 'Generated'}: ${new Date().toLocaleString('en-GB')}    ${isRTL ? 'إجمالي الأصناف' : 'Total Items'}: ${result.totalItems}    ${isRTL ? 'مشبوهة' : 'Suspicious'}: ${result.totalSuspicious}    ${isRTL ? 'أقصى عدد وحدات' : 'Max Units'}: ${maxUnits}`;
      ws.getCell('A4').alignment = { horizontal: 'center' };
      ws.getCell('A4').font = { italic: true, size: 11 };

      const hr = ws.getRow(6);
      headers.forEach((v, i) => {
        const c = hr.getCell(i + 1);
        c.value = v;
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
        c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        c.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
      });

      let rowIdx = 7;
      filteredRows.forEach(r => {
        const xr = ws.getRow(rowIdx);
        xr.getCell(1).value = r.itemCode;
        xr.getCell(2).value = r.itemName;
        xr.getCell(3).value = r.itemNameAr;
        xr.getCell(4).value = isRTL ? (r.categoryNameAr || r.categoryName) : (r.categoryName || r.categoryNameAr);
        xr.getCell(5).value = isRTL ? (r.baseUnitNameAr || r.baseUnitName) : r.baseUnitName;

        for (let i = 0; i < maxUnits; i++) {
          const u = r.units[i];
          const colBase = 6 + i * 5;
          if (u) {
            xr.getCell(colBase).value = isRTL ? (u.unitNameAr || u.unitName) : u.unitName;
            xr.getCell(colBase + 1).value = u.isBase ? '-' : Number(u.stepFactor);
            xr.getCell(colBase + 2).value = u.isBase
              ? (isRTL ? '(الأساسية)' : '(Base)')
              : (isRTL ? (u.nextUnitNameAr || u.nextUnitName || '') : (u.nextUnitName || ''));
            xr.getCell(colBase + 3).value = Number(u.salePrice);
            xr.getCell(colBase + 4).value = Number(u.purchasePrice);
          } else {
            for (let j = 0; j < 5; j++) xr.getCell(colBase + j).value = '';
          }
        }

        const tailStart = 6 + maxUnits * 5;
        const topU = r.units[0];
        xr.getCell(tailStart).value = topU ? Number(topU.conversionFactor) : '';
        xr.getCell(tailStart + 1).value = r.warningCode ? (isRTL ? r.warningMessageAr : r.warningMessage) : '';
        xr.getCell(tailStart + 2).value = '';

        if (r.warningCode) {
          xr.eachCell({ includeEmpty: true }, c => {
            c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3CD' } };
          });
          xr.getCell(tailStart + 1).font = { color: { argb: 'FFB71C1C' }, bold: true };
        }
        xr.eachCell({ includeEmpty: true }, c => {
          c.border = { top: { style: 'hair' }, bottom: { style: 'hair' }, left: { style: 'hair' }, right: { style: 'hair' } };
        });
        rowIdx++;
      });

      const widths: { width: number }[] = [
        { width: 14 }, { width: 28 }, { width: 28 }, { width: 22 }, { width: 14 }
      ];
      for (let i = 0; i < maxUnits; i++) {
        widths.push({ width: 14 }, { width: 12 }, { width: 14 }, { width: 12 }, { width: 12 });
      }
      widths.push({ width: 14 }, { width: 40 }, { width: 28 });
      ws.columns = widths;

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ItemsUnitsReport_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch {
      toast.error(isRTL ? 'فشل' : 'Failed');
    }
  };

  const exportToPdf = () => {
    if (!result) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.width;
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(21, 101, 192);
      doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(21, 101, 192); doc.roundedRect(pw / 2 - 70, 32, 140, 10, 3, 3, 'F');
      doc.setFontSize(12); doc.setTextColor(255, 255, 255);
      doc.text('Items & Units of Measure Report', pw / 2, 38.5, { align: 'center' });

      autoTable(doc, {
        head: [['Code', 'Name', 'Category', 'Base', 'Units Chain', 'Top CF', 'Warning']],
        body: filteredRows.map(r => [
          r.itemCode,
          r.itemName,
          r.categoryName,
          r.baseUnitName,
          r.units.map(u => u.isBase
            ? `${u.unitName}(Base)`
            : `${u.unitName}=${fmtNum(u.stepFactor)} ${u.nextUnitName || ''}`).join(' → '),
          r.units[0] ? fmtNum(r.units[0].conversionFactor) : '-',
          r.warningCode ? (r.warningMessage || '') : ''
        ]),
        startY: 50,
        theme: 'grid',
        headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 7 },
        columnStyles: { 4: { cellWidth: 90 }, 6: { cellWidth: 50 } },
        didParseCell: (data) => {
          if (data.section === 'body' && data.row.raw && (data.row.raw as any[])[6]) {
            data.cell.styles.fillColor = [255, 243, 205];
          }
        }
      });

      doc.save(`ItemsUnitsReport_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch {
      toast.error(isRTL ? 'فشل' : 'Failed');
    }
  };

  const handlePrint = () => {
    if (!result) return;
    const w = window.open('', '_blank', 'width=1200,height=800'); if (!w) return;

    const chainHtml = (r: ReportRow) => r.units.map(u => {
      const name = isRTL ? (u.unitNameAr || u.unitName) : u.unitName;
      const next = isRTL ? (u.nextUnitNameAr || u.nextUnitName) : u.nextUnitName;
      if (u.isBase) {
        return `<div><b>${name}</b> <span style="color:#0D47A1">(${isRTL ? 'الأساسية' : 'Base'} = 1)</span> &nbsp; <span style="color:#666;font-size:9px">[${fmtNum(u.salePrice)} / ${fmtNum(u.purchasePrice)}]</span></div>`;
      }
      const factorTxt = next
        ? `= <b>${fmtNum(u.stepFactor)}</b> ${next}`
        : `= <b>${fmtNum(u.conversionFactor)}</b> ${isRTL ? r.baseUnitNameAr || r.baseUnitName : r.baseUnitName}`;
      return `<div><b>${name}</b> ${factorTxt} &nbsp; <span style="color:#666;font-size:9px">[${fmtNum(u.salePrice)} / ${fmtNum(u.purchasePrice)}]</span></div>`;
    }).join('');

    const rowsHtml = filteredRows.map(r => `
      <tr${r.warningCode ? ' style="background:#FFF3CD"' : ''}>
        <td>${r.itemCode}</td>
        <td>${isRTL ? r.itemNameAr : r.itemName}</td>
        <td>${isRTL ? (r.categoryNameAr || r.categoryName) : (r.categoryName || r.categoryNameAr)}</td>
        <td>${isRTL ? (r.baseUnitNameAr || r.baseUnitName) : r.baseUnitName}</td>
        <td>${chainHtml(r)}</td>
        <td style="text-align:center;font-weight:bold;color:${r.warningCode === 'LOW_CF' ? '#B71C1C' : '#1565C0'}">${r.units[0] ? fmtNum(r.units[0].conversionFactor) : '-'}</td>
        <td style="color:#B71C1C;font-weight:bold;font-size:9px">${r.warningCode ? (isRTL ? r.warningMessageAr : r.warningMessage) : ''}</td>
      </tr>`).join('');

    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:15px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #1565C0;padding-bottom:10px;margin-bottom:10px}
      .header img{max-height:60px} .header h1{color:#1565C0;margin:0;font-size:20px}
      table{width:100%;border-collapse:collapse;font-size:10px;margin-top:6px}
      th{background:#1565C0;color:white;padding:6px;text-align:${isRTL ? 'right' : 'left'};border:1px solid #1565C0;font-size:10px}
      td{padding:6px;border:1px solid #ddd;vertical-align:top}
      td > div { padding: 2px 0; border-bottom: 1px dotted #eee; }
      td > div:last-child { border-bottom: none; }
      .stats{display:flex;gap:14px;justify-content:center;margin:10px 0;font-size:11px}
      .stats span{padding:5px 12px;background:#E3F2FD;border-radius:14px;color:#0D47A1;font-weight:bold}
      @media print{body{margin:8px}}
    </style></head><body>
    <div class="header">
      <div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
      <div style="text-align:center;flex:1">
        <h1>${reportTitle}</h1>
        <div style="color:#666;font-size:11px">${compName || ''}</div>
      </div>
      <div style="width:80px"></div>
    </div>
    <div class="stats">
      <span>${isRTL ? 'إجمالي الأصناف' : 'Total Items'}: ${result.totalItems}</span>
      <span>${isRTL ? 'أصناف مشبوهة' : 'Suspicious'}: ${result.totalSuspicious}</span>
      <span>${isRTL ? 'أقصى عدد وحدات' : 'Max Units'}: ${maxUnits}</span>
      <span>${isRTL ? 'تاريخ التقرير' : 'Generated'}: ${new Date().toLocaleString('en-GB')}</span>
    </div>
    <table>
      <thead><tr>
        <th>${isRTL ? 'الكود' : 'Code'}</th>
        <th>${isRTL ? 'الصنف' : 'Item'}</th>
        <th>${isRTL ? 'المجموعة' : 'Category'}</th>
        <th>${isRTL ? 'الوحدة الأساسية' : 'Base Unit'}</th>
        <th>${isRTL ? 'سلسلة الوحدات (وحدة = ك ع من الأصغر) [بيع/شراء]' : 'Units Chain (unit = N of next) [Sale/Purch]'}</th>
        <th>${isRTL ? 'CF تراكمي' : 'Top CF'}</th>
        <th>${isRTL ? 'تحذير' : 'Warning'}</th>
      </tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <div style="text-align:center;margin-top:10px;color:#999;font-size:9px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {reportTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRTL ? 'سلسلة وحدات القياس لكل صنف بكامل عمقها (الأكبر → الأساسية) لاكتشاف أي خطأ في معاملات التحويل' : 'Full unit chain per item (largest → base) to detect conversion-factor mistakes'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={loadReport} disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', px: 3, borderRadius: 3 }}>
            {loading ? (isRTL ? 'جاري...' : 'Loading...') : (isRTL ? 'عرض' : 'Load')}
          </Button>
          {result && (<>
            <Button variant="contained" size="small" startIcon={<ExcelIcon />} onClick={exportToExcel}
              sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white', borderRadius: 3 }}>Excel</Button>
            <Button variant="contained" size="small" startIcon={<PdfIcon />} onClick={exportToPdf}
              sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white', borderRadius: 3 }}>PDF</Button>
            <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={handlePrint}
              sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white', borderRadius: 3 }}>
              {isRTL ? 'طباعة' : 'Print'}
            </Button>
          </>)}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControlLabel control={<Checkbox checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} />}
          label={isRTL ? 'إظهار الأصناف غير النشطة' : 'Include Inactive'} />
        <FormControlLabel control={<Checkbox checked={onlySuspicious} onChange={e => setOnlySuspicious(e.target.checked)} />}
          label={isRTL ? 'الأصناف المشبوهة فقط' : 'Suspicious Only'} />
      </Box>

      {result && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'إجمالي الأصناف' : 'Total Items'}</Typography>
                <Typography variant="h4" fontWeight="bold">{result.totalItems}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #C62828 0%, #EF5350 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'أصناف مشبوهة' : 'Suspicious'}</Typography>
                <Typography variant="h4" fontWeight="bold">{result.totalSuspicious}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #6A1B9A 0%, #AB47BC 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'أقصى عدد وحدات/صنف' : 'Max Units / Item'}</Typography>
                <Typography variant="h4" fontWeight="bold">{result.maxUnitsPerItem}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'حسب الوحدة الأساسية' : 'By Base Unit'}</Typography>
                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {Object.entries(result.baseUnitDistribution).map(([k, v]) => `${k}: ${v}`).join('  •  ')}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder={isRTL ? 'بحث بالكود/الاسم/المجموعة/الباركود...' : 'Search code/name/category/barcode...'}
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              sx={{ minWidth: 380 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
            <Typography variant="body2" color="text.secondary">
              {isRTL ? `${filteredRows.length} صنف معروض` : `${filteredRows.length} items shown`}
            </Typography>
          </Box>

          {filteredRows.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary">{isRTL ? 'لا توجد بيانات' : 'No data'}</Typography>
            </Paper>
          ) : (
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: '72vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الكود' : 'Code'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'المجموعة' : 'Category'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الأساسية' : 'Base'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', minWidth: 360 }}>
                        {isRTL ? 'سلسلة الوحدات [الأكبر → الأساسية]' : 'Units Chain [largest → base]'}
                      </TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'CF تراكمي' : 'Top CF'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', minWidth: 220 }}>{isRTL ? 'تحذير' : 'Warning'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((r) => (
                      <TableRow key={r.itemId}
                        sx={{
                          bgcolor: r.warningCode ? (isDark ? 'rgba(255,167,38,0.18)' : '#FFF3CD') : 'inherit',
                          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                          verticalAlign: 'top'
                        }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.itemCode}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{isRTL ? r.itemNameAr : r.itemName}</Typography>
                          {r.barcode && <Typography variant="caption" color="text.secondary">{r.barcode}</Typography>}
                        </TableCell>
                        <TableCell>{isRTL ? (r.categoryNameAr || r.categoryName) : (r.categoryName || r.categoryNameAr)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={isRTL ? (r.baseUnitNameAr || r.baseUnitName) : r.baseUnitName}
                            sx={{ bgcolor: '#E3F2FD', color: '#0D47A1', fontWeight: 'bold' }} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {r.units.map((u, idx) => {
                              const name = isRTL ? (u.unitNameAr || u.unitName) : u.unitName;
                              const next = isRTL ? (u.nextUnitNameAr || u.nextUnitName) : u.nextUnitName;
                              const isLast = idx === r.units.length - 1;
                              return (
                                <Box key={u.unitId}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip size="small" label={name}
                                      sx={{
                                        bgcolor: u.isBase ? '#E3F2FD' : '#FFF3E0',
                                        color: u.isBase ? '#0D47A1' : '#E65100',
                                        fontWeight: 'bold',
                                        minWidth: 70
                                      }} />
                                    {u.isBase ? (
                                      <Typography variant="caption" sx={{ color: '#0D47A1', fontWeight: 'bold' }}>
                                        ({isRTL ? 'الأساسية' : 'Base'} = 1)
                                      </Typography>
                                    ) : next ? (
                                      <Typography variant="body2" component="span">
                                        = <b style={{ color: u.stepFactor < 4 ? '#B71C1C' : '#1565C0' }}>{fmtNum(u.stepFactor)}</b> {next}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" component="span">
                                        = <b style={{ color: '#1565C0' }}>{fmtNum(u.conversionFactor)}</b> {isRTL ? r.baseUnitNameAr || r.baseUnitName : r.baseUnitName}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                      [{isRTL ? 'بيع' : 'Sale'}: {fmtUnitPrice(u.salePrice)} / {isRTL ? 'شراء' : 'Purch'}: {fmtUnitPrice(u.purchasePrice)}]
                                    </Typography>
                                  </Box>
                                  {!isLast && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 2, color: 'text.disabled' }}>
                                      <ArrowDownIcon sx={{ fontSize: 14 }} />
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: r.warningCode === 'LOW_CF' ? '#B71C1C' : '#1565C0' }}>
                          {r.units[0] ? fmtNum(r.units[0].conversionFactor) : '-'}
                        </TableCell>
                        <TableCell>
                          {r.warningCode && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <WarningIcon sx={{ color: '#B71C1C', fontSize: 18 }} />
                              <Typography variant="caption" sx={{ color: '#B71C1C', fontWeight: 'bold' }}>
                                {isRTL ? r.warningMessageAr : r.warningMessage}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {!result && !loading && (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <InventoryIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {isRTL ? 'اضغط "عرض" لتحميل تقرير الأصناف ووحدات القياس' : 'Click "Load" to load the items & units report'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ItemsUnitsReport;
